import { FC, useEffect } from "react";

import { useAv } from "../../../utils/av";

import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { MenuItem } from "mds/components/Menu";
import { HandRaiseIcon, HandRaiseOutlineIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentClassUserId } from "store/selectors";
import { selectIsVirtualUserSpeaking } from "store/selectors/pubsub-virtual-class-state";
import { selectIsLargeClass } from "store/selectors/virtual-class-users-sorting";

const LOWER_HAND_TIMEOUT_DURATION_MS = 3000;

export const HandRaiseButton: FC<{ isMobile?: boolean; isMenuItem?: boolean }> = ({
  isMobile = false,
  isMenuItem = false,
}) => {
  const { isHandRaised, toggleHand } = useAv();
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  // In a large class, we don't want to lower the hand automatically as it may deactivate the participant if they don't have time to speak.
  const isLargeClass = useAppSelector(selectIsLargeClass);
  const isSpeaking = useAppSelector((state) =>
    currentClassUserId ? selectIsVirtualUserSpeaking(state, currentClassUserId) : false,
  );

  useEffect(() => {
    if (!isHandRaised || !isSpeaking || isLargeClass) return;
    const timeoutId = setTimeout(() => toggleHand(), LOWER_HAND_TIMEOUT_DURATION_MS);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHandRaised, isSpeaking]);

  if (isMenuItem) {
    return (
      <MenuItem eventName={isHandRaised ? "Lower hand" : "Raise hand"} onClick={toggleHand}>
        <IconText
          iconStart={isHandRaised ? <HandRaiseIcon /> : <HandRaiseOutlineIcon />}
          text={isHandRaised ? t("av.lower_hand") : t("av.raise_hand")}
        />
      </MenuItem>
    );
  }

  return (
    <Button
      eventName={isHandRaised ? "Lower hand" : "Raise hand"}
      isSelected={isHandRaised}
      kind="squareLegacy"
      size={isMobile ? "xl" : "l"}
      title={t(isHandRaised ? "av.lower_hand" : "av.raise_hand")}
      isSquare
      onClick={toggleHand}
    >
      {isHandRaised ? (
        <HandRaiseIcon size={isMobile ? "l" : "m"} />
      ) : (
        <HandRaiseOutlineIcon size={isMobile ? "l" : "m"} />
      )}
    </Button>
  );
};
